/**
 * TCS — keeps keyboard focus out from under the stuck sticky Add to cart bar.
 * Finding A11Y-LA-05, ruling R-033 (CTO, 2026-09-21).
 *
 * WHY THIS EXISTS
 * Horizon's sticky Add to cart bar is `position: fixed` at the bottom of the
 * viewport. Once it is stuck, a keyboard user tabbing through the content below
 * the buy box (accordions, rails, reviews) reaches controls that the bar paints
 * over completely: the browser has already scrolled them into the viewport, so
 * it does not scroll again, and the focus ring is simply not visible.
 *
 * Measured by `docs/accessibility/evidence/2026-09-18/lane-a/probes/
 * sticky-focus-obscured.probe.mjs` on real theme CSS with the bar stuck:
 * WebKit 26.6 reported 18 of 39 focused links entirely under the bar at
 * 390x844 and 23 of 39 at 390x500; Chromium 153 reported 1 of 39 at each on
 * that probe run (why the two engines differ so much is UNEXPLAINED — not investigated, and
 * the counts move by a few between runs — see the LA-05 evidence record; the
 * measurement that matters is that the AFTER column is 0 on every run in both
 * engines). Desktop (1280x800 / 1280x480): the probe counted 0
 * entirely obscured links there and this script makes no adjustment there. That
 * is the script doing nothing, NOT a verified desktop fix: desktop is out of
 * scope per R-033 and is neither fixed nor testable here (see the
 * `window.scrollBy` limit below). The reasoning that a centred 600px pill leaves
 * full-width controls partly visible, which 2.4.11 AA allows, is an argument,
 * not a measurement of any control type beyond the fixture's. Standard: WCAG 2.4.11 Focus Not Obscured (Minimum),
 * AA. Storefront and VoiceOver confirmation are still outstanding.
 *
 * WHY NOT CSS, AND WHY NOT A CORE EDIT
 * `scroll-padding-block-end` on the root was tried at 108px and changed nothing
 * in either engine: scroll padding only affects a scroll the browser performs,
 * and neither engine scrolls an element that is ALREADY inside the viewport.
 * R-033 deliberately did NOT extend R-014: `assets/sticky-add-to-cart.js` keeps
 * its single selector substitution and the owned list stays where it is. The
 * bar already publishes everything this needs (`data-stuck` on
 * `.sticky-add-to-cart__bar`), so no component internal is touched.
 *
 * WHAT IT DOES — the whole behaviour, and its limits
 * On `focusin`, in the next animation frame (one adjustment per frame, latest
 * target wins, never a loop), and only when ALL of these hold:
 *   - a `.sticky-add-to-cart__bar[data-stuck="true"]` exists inside
 *     `#MainContent[data-template^="product"]`;
 *   - the bar does not contain the focused element;
 *   - that element is still `document.activeElement`;
 *   - it matches `:focus-visible` — see POINTER FOCUS below for exactly which
 *     controls that does and does not exclude;
 *   - no MODAL `<dialog>` is open (`dialog[open]:modal`; any open dialog in an
 *     engine that rejects `:modal`), and the element is not inside any dialog —
 *     this is what keeps it out of the zoom viewer, the PDP image viewer,
 *     quick-add and the cart drawer, all of which are native modal dialogs. A
 *     non-modal open dialog elsewhere on the page does not switch the fix off;
 *   - the two bounding rectangles intersect AND the bar spans the part of the
 *     focused box that is inside the viewport horizontally (see below);
 * it scrolls the DOCUMENT down by the smallest amount that lifts the focused
 * box clear of the bar (plus RING_CLEARANCE for the focus ring itself),
 * clamped so it never pushes the element's own top edge above the viewport
 * (a box taller than the free space stays anchored at the top) and never
 * past the end of the document. Nothing else: no `scrollIntoView`, no focus
 * move, no DOM or style change, no `IntersectionObserver` — Horizon's own
 * visibility logic decides when the bar sticks and is left completely alone.
 *
 * POINTER FOCUS — what `:focus-visible` excludes, per R-033a (measured, not assumed)
 * R-033 originally said this meant "a tap or a mouse click never scrolls the
 * page". That claim is WITHDRAWN by R-033a and replaced by: pointer activation
 * of a BUTTON or LINK never scrolls; a pointer-focused FORM FIELD is cleared,
 * deliberately. Measured at 390x844 in Chromium 153 and WebKit 26.6, raw mouse
 * click and raw touch tap, on a 60px control parked with a 20px sliver above
 * the bar's top edge — identical in both engines, both input methods:
 *   - `<button>`, `<a href>`: `:focus-visible` false -> 0 adjustments. (In the
 *     WebKit build measured, focus did not move to them on a click at all;
 *     `document.activeElement` stayed unchanged. Measured under Playwright,
 *     which is not Safari — this says nothing about Safari or iOS.)
 *   - `<input type="text">`, `type="email"`, `type="number"`, a READONLY text
 *     input, `<textarea>`, `<select>`: `:focus-visible` true -> 1 adjustment.
 *   - a DISABLED input is not focusable, so nothing fires; out of scope.
 * Readonly fields stay in scope on purpose: they take a caret, a selection and
 * arrow keys. The field behaviour is intended, not tolerated — a tap on a field
 * starts an interaction that continues with typing, and on iOS the keyboard is
 * about to cover the same band, so the field wants to be clear of the bar
 * before the first keystroke. It is also what keeps a keyboard user who reaches
 * the same field from being treated differently. The suite asserts the matrix
 * per control type so this cannot drift unnoticed.
 *
 * WHY THE BAR MUST COVER THE VISIBLE PART — the one narrowing of R-033
 * R-033 grants "the rectangles intersect". This adds one condition: the bar
 * must also cover the part of the focused box that is INSIDE THE VIEWPORT,
 * horizontally (within EDGE_TOLERANCE). The visible part is the box clipped to
 * 0..`clientWidth`, so a control wider than the viewport (a carousel or rail
 * slide, a wide table cell) hidden under the full-bleed bar IS cleared; testing
 * the full box width would skip it, because the overhang lies outside the bar.
 * Up to 989px that is not a narrowing at all: the bar is full-bleed `width: 100%`
 * there, so it covers every visible part and the condition reduces to the
 * granted one. Measured bar
 * width at 390/749/750/900/989 = the viewport width, in both engines; only the
 * bottom offset changes (0 below 750px, 20px from 750px). That full-bleed rule
 * at 750-989px is OUR OWN — `assets/tcs-pdp-core.css`, inside
 * `@media screen and (max-width: 989px)` — not Horizon's; Horizon's section CSS
 * would give a 600px pill from 750px up. The centred 600px pill therefore starts
 * at 990px, not 750px, and at 990px+ the probe counted 0 entirely obscured
 * controls and R-033 puts desktop out of scope. Without this condition the plain
 * rectangle test fires on any content-width control whose line merely crosses
 * the pill's band: measured in the suite fixture at 1280x800, that moved the
 * page on focus steps where nothing was obscured. Nothing at 989px or below
 * changes.
 *
 * Those pixel widths are DESCRIPTION, not configuration. Per R-033a the scope
 * is bar geometry, never a breakpoint: this file contains no width constant and
 * no media query, it only compares the bar's rectangle with the visible part of
 * the focused box as they are at that moment. The one viewport read is
 * `clientWidth`, used to clip the box to what the shopper can see — it is a
 * measurement of the visible area, not a threshold anyone compares against.
 * Restyle the bar and the behaviour follows the new geometry by itself. The
 * conformance test greps for the obvious reintroductions — a three-digit
 * literal, `matchMedia`, `innerWidth`, `visualViewport` — which
 * narrows the hole but does not close it; a grep cannot prove the absence of a
 * breakpoint, only the absence of the ones it names. The suite exercises the
 * behaviour at nine widths, which is the real check.
 *
 * ONE ADJUSTMENT PER FOCUS CHANGE — why this cannot loop
 * A burst of `focusin` events collapses into a single animation frame and the
 * last target wins, so the handler runs at most once per frame. Inside that
 * frame it re-reads BOTH rectangles immediately before scrolling, so anything
 * that moved the page in between — the UA's own focus scrolling, another
 * script, the shopper — is already accounted for and the adjustment is either
 * recomputed or dropped. `scrollBy` moves no focus and fires no `focusin`, so
 * the handler cannot re-enter itself; an external scroll cannot drive it into a
 * loop, because a scroll alone never schedules it.
 *
 * Known limits, recorded rather than worked around:
 * - if focus moves DURING the bar's 0.3s slide-in transition, the rectangle read
 *   here is the mid-transition one, so the clearance can fall short for that one
 *   focus step. UNMEASURED: neither the shortfall nor whether a later focus move
 *   recovers it has been observed; the suite runs with reduced motion, which
 *   removes the transition. Worth a probe if it is ever seen in the wild.
 * - `window.scrollBy` scrolls the DOCUMENT. READ FROM CSS, NOT MEASURED:
 *   `assets/base.css` gives `.page-wrapper` `height: 100dvh; overflow-y: auto`
 *   and sets `overflow: hidden` on html/body inside `@media (min-width: 990px)`,
 *   which reads as the document no longer being the scroll container at those
 *   widths. What IS measured is that at 990px and up this script makes zero
 *   adjustments, because the bar is then the centred pill and never spans a
 *   content-width control — so the CSS reading has no consequence today and was
 *   never exercised. If the desktop pill is ever ruled in scope, confirm the
 *   scroll container there first; do not assume `window.scrollBy` reaches it.
 * `behavior: 'instant'` is load-bearing: base.css sets
 * `html { scroll-behavior: smooth }`, and an animated clearance would both lag
 * the focus ring and ignore a reduced-motion preference.
 *
 * COUPLING NOTE: assets/sticky-add-to-cart.js, Horizon 4.1.3, baseline
 * 25426165ade9ce5e, owned fd0cff8f8fa93433 (R-014).
 * Relies on the bar's PUBLISHED state contract only: `#showStickyBar()` setting
 * `data-stuck="true"` and `#hideStickyBar()` setting `data-stuck="false"` on
 * `[ref="stickyBar"]`, and that value being restored after a variant morph.
 * Nothing in sticky-add-to-cart.js is patched, overridden, observed or read
 * beyond that attribute.
 *
 * Two further terms of the entry selector come from OTHER files: the
 * `.sticky-add-to-cart__bar` class (`sections/product-information.liquid`) and
 * `#MainContent[data-template]` (`layout/theme.liquid`). BOTH of those files
 * are owned — R-022 and R-023 — and `check-horizon.mjs` skips every owned file
 * outright (`if (owned[f]) continue`), so its green tick says nothing about
 * either. The tripwire is the only gate that reads them, and the browser suite
 * cannot stand in: it hand-writes its own `<main>`, so it passes even when the
 * real layout can no longer match. Demonstrated by mutation on 2026-09-21.
 * Tripwire: scripts/tests/tcs-sticky-focus-clearance-coupling.test.mjs.
 * Loaded deferred by tcs-head on product templates only.
 */
(() => {
  const BAR = '#MainContent[data-template^="product"] .sticky-add-to-cart__bar[data-stuck="true"]';
  // Space kept between the focused box and the bar so the focus ring (2px
  // outline plus its offset in Horizon's base styles) clears it too.
  const RING_CLEARANCE = 8;
  // Slack on the horizontal span test, for a visible part that overhangs the
  // full-bleed bar by a sub-pixel — a fractional rect, a 0.5px border, a box bled to the
  // viewport edge. UNTESTED INSURANCE: instrumented across 40 controls at six
  // widths in both engines, the closest left margin at 989px and below was
  // exactly 0.00px (so the cases pass with or without this slack) and the
  // nearest miss at 990px and above was 195px and 340px (so it admits nothing
  // there either). It changes no measured case today; it is here for the
  // fractional geometry the fixture does not produce.
  const EDGE_TOLERANCE = 1;

  // Only a MODAL dialog makes the page behind it inert and owns the scroll. A
  // non-modal open dialog (a disclosure, a toast) must not switch the fix off.
  // An engine that throws on `:modal` gets the older, more conservative answer:
  // any open dialog counts.
  const modalDialogOpen = () => {
    try {
      return document.querySelector('dialog[open]:modal') !== null;
    } catch {
      return document.querySelector('dialog[open]') !== null;
    }
  };

  /** @type {Element | null} */
  let queued = null;

  /** @param {Element} target */
  const clear = (target) => {
    if (!target.isConnected || document.activeElement !== target) return;

    const bar = document.querySelector(BAR);
    if (!bar || bar.contains(target)) return;

    // Pointer focus must never move the page under the shopper.
    if (!target.matches(':focus-visible')) return;

    // Never inside, or underneath, an open dialog: the zoom viewer, the PDP
    // image viewer, quick-add and the cart drawer are all native modals, and
    // scrolling the page behind one is never the right answer.
    if (target.closest('dialog') || modalDialogOpen()) return;

    const root = document.scrollingElement ?? document.documentElement;
    const barRect = bar.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    // The rectangles must intersect (R-033), AND the bar must cover the part of
    // the focused box that is inside the viewport — see WHY THE BAR MUST COVER
    // THE VISIBLE PART, above. A control wider than the viewport (a rail slide,
    // a wide cell) is judged on the part the shopper can see.
    const visibleLeft = Math.max(rect.left, 0);
    const visibleRight = Math.min(rect.right, root.clientWidth);
    const overlapsVertically = rect.bottom > barRect.top && rect.top < barRect.bottom;
    const spansHorizontally =
      visibleRight > visibleLeft &&
      visibleLeft >= barRect.left - EDGE_TOLERANCE &&
      visibleRight <= barRect.right + EDGE_TOLERANCE;
    if (!overlapsVertically || !spansHorizontally) return;

    const remaining = root.scrollHeight - root.clientHeight - root.scrollTop;
    const delta = Math.min(
      rect.bottom - barRect.top + RING_CLEARANCE,
      Math.max(rect.top, 0),
      remaining,
    );
    if (delta <= 0) return;

    window.scrollBy({ top: delta, behavior: 'instant' });
  };

  document.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // One adjustment per frame: a burst of focus changes collapses to the last
    // one, and the handler can never re-enter itself.
    const scheduled = queued !== null;
    queued = target;
    if (scheduled) return;
    requestAnimationFrame(() => {
      const element = queued;
      queued = null;
      if (element) clear(element);
    });
  });
})();
